/**
 * ALPINE.JS DEVELOPMENT DIRECTIVE
 *
 * Universal Principles (applies to all Alpine components and stores):
 *
 * 1. Unidirectional Data Flow: State changes should flow in one direction.
 *    User interaction → Update state → Render/reconcile UI
 *
 * 2. Explicit State Management: State should live in Alpine data/store properties,
 *    not scattered across DOM attributes or hidden in closure variables.
 *
 * 3. Separation of Concerns:
 *    - State updates (synchronous)
 *    - DOM manipulation (synchronous, derives from state)
 *    - Async operations (fetch, with proper guards)
 *
 * 4. Idempotent Operations: Functions that render/reconcile UI should be safe to call
 *    multiple times with the same state without unintended side effects.
 *
 * 5. Single Responsibility: Each function should have one clear purpose.
 *    Avoid functions that both update state AND fetch data AND manipulate DOM.
 *
 * URL-as-SSOT Pattern (applies to features with shareable/persistent state):
 *
 * For features requiring deep links, bookmarks, browser history, or server coordination
 * (e.g., collection filters, sort order, pagination):
 *
 * 1. URL Query Parameters as Single Source of Truth: Never read DOM state to determine
 *    application state. Always read from window.location.search or URLSearchParams.
 *
 * 2. URL-First Updates: User interaction → Update URL immediately → Reconcile all UI
 *    elements from new URL state → Fetch server data using URL params.
 *
 * 3. State Reconciliation: Any function that modifies URL params MUST call a reconciliation
 *    function to sync all DOM elements to match the new URL state.
 *
 * 4. No Direct DOM Reads for State: Event handlers must calculate state changes from the
 *    event target only, not by querying multiple DOM elements to derive state.
 *
 * 5. Idempotent Reconciliation: Reconciliation functions must produce identical DOM output
 *    when called multiple times with the same URL params.
 *
 * See: wwc-2025/REFACTOR-FILTERS.md for collection filters refactor plan.
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('header', () => ({
    get breakpoint() {
      return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-tablet')) || 768;
    },
    searchOpen: window.innerWidth >= 768,
    menuOpen: false,

    init() {
      this.searchOpen = window.innerWidth >= this.breakpoint;
      window.addEventListener('resize', () => {
        this.searchOpen = window.innerWidth >= this.breakpoint;
      });
    }
  }));

  Alpine.store('cart', {
    itemCount: window.cartData?.item_count || 0,
    items: window.cartData?.items || [],
    total: window.cartData?.total_price || 0,
    pendingQuantities: {},
    processing: {},
    retryCount: {},
    maxRetries: 5,

    async addToCart(variantId, quantity = 1) {
      try {
        await this._performCartOperation('/cart/add.js', { id: variantId, quantity });
        Alpine.store('cartDrawer').show();
      } catch (error) {
        console.error('Cart add error:', error);
      }
    },

    formatMoney(cents) {
      return new Intl.NumberFormat('en-IE', {
        currency: 'EUR',
        style: 'currency'
      }).format(cents / 100);
    },

    async _performCartOperation(endpoint, payload) {
      const response = await fetch(endpoint, {
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Cart operation failed: ${response.status}`);
      }

      await this.refreshCart();
      return response;
    },

    async refreshCart() {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.itemCount = cart.item_count || 0;
      this.total = cart.total_price || 0;
      this.items = cart.items || [];
    },

    async updateQuantity(key, quantity) {
      try {
        await this._performCartOperation('/cart/change.js', { id: key, quantity });
      } catch (error) {
        console.error('Cart update error:', error);
      }
    },

    getItemByVariantId(variantId) {
      return this.items.find(item => item.variant_id === variantId);
    },

    addOrUpdateQuantity(variantId, targetQuantity) {
      this.pendingQuantities[variantId] = targetQuantity;

      if (!this.processing[variantId]) {
        this._processQueue(variantId);
      }
    },

    async _processQueue(variantId) {
      this.processing[variantId] = true;
      this.retryCount[variantId] = 0;

      while (this.pendingQuantities[variantId] !== undefined) {
        const targetQuantity = this.pendingQuantities[variantId];
        delete this.pendingQuantities[variantId];

        try {
          const existingItem = this.getItemByVariantId(variantId);

          if (!existingItem && targetQuantity === 0) {
            continue;
          }

          const endpoint = existingItem ? '/cart/change.js' : '/cart/add.js';
          const payload = existingItem
            ? { id: existingItem.key, quantity: targetQuantity }
            : { id: variantId, quantity: targetQuantity };

          await this._performCartOperation(endpoint, payload);
          this.retryCount[variantId] = 0;
        } catch (error) {
          console.error('Cart operation error:', error);

          if (this.retryCount[variantId] < this.maxRetries) {
            this.retryCount[variantId]++;
            const delay = Math.min(1000 * Math.pow(2, this.retryCount[variantId] - 1), 8000);
            await new Promise(resolve => setTimeout(resolve, delay));
            this.pendingQuantities[variantId] = targetQuantity;
          } else {
            console.error(`Max retries reached for variant ${variantId}`);
            this.retryCount[variantId] = 0;
          }
        }
      }

      this.processing[variantId] = false;
      delete this.retryCount[variantId];
    },
  });

  Alpine.store('cartDrawer', {
    open: false,

    close() {
      this.open = false;
    },
    show() {
      this.open = true;
    }
  });

  Alpine.store('collectionFilters', {
    filterCache: null,
    urlChanged: false,

    init() {
      window.addEventListener('popstate', () => {
        this.urlChanged = !this.urlChanged;
        const urlParams = new URLSearchParams(window.location.search);
        this.reconcileFromURL();
        this.fetchAndUpdateProducts(urlParams.toString());
      });
    },

    handleCheckboxChange(event) {
      const checkbox = event.target;
      const urlParams = new URLSearchParams(window.location.search);
      const name = checkbox.name;
      const value = checkbox.value;

      if (checkbox.checked) {
        urlParams.append(name, value);
      } else {
        const values = urlParams.getAll(name).filter(v => v !== value);
        urlParams.delete(name);
        values.forEach(v => urlParams.append(name, v));
      }

      this.updateURLAndFetch(urlParams);
    },

    handleABVChange(event, minABV, maxABV) {
      const checkbox = event.target;
      const urlParams = new URLSearchParams(window.location.search);
      const abvContainer = checkbox.closest('.filter-group__content');
      const hiddenInputs = abvContainer.querySelectorAll('input[data-abv]');

      hiddenInputs.forEach(input => {
        const paramName = input.name;
        urlParams.delete(paramName);
      });

      if (checkbox.checked) {
        hiddenInputs.forEach(input => {
          const abv = parseFloat(input.dataset.abv);
          const paramName = input.name;
          const paramValue = input.value;

          if (abv >= minABV && abv <= maxABV) {
            urlParams.append(paramName, paramValue);
          }
        });
      }

      this.updateURLAndFetch(urlParams);
    },

    handlePriceChange(event, minPrice, maxPrice, minParamName, maxParamName) {
      const checkbox = event.target;
      const urlParams = new URLSearchParams(window.location.search);

      urlParams.delete(minParamName);
      urlParams.delete(maxParamName);

      if (checkbox.checked) {
        urlParams.set(minParamName, minPrice);
        if (maxPrice !== null) {
          urlParams.set(maxParamName, maxPrice);
        }
      }

      this.updateURLAndFetch(urlParams);
    },

    async initFilters() {
      await this.cacheFullFilterStructure();
      this.reconcileFromURL();

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.toString()) {
        await this.fetchAndUpdateProducts(urlParams.toString());
      }
    },

    async cacheFullFilterStructure() {
      if (this.filterCache) return;

      const collectionSection = document.querySelector('.collection')?.closest('.shopify-section');
      const sectionId = collectionSection ? collectionSection.id.replace('shopify-section-', '') : null;

      if (!sectionId) return;

      const fullFilterUrl = `${window.location.pathname}?section_id=${sectionId}`;
      const response = await fetch(fullFilterUrl);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      this.filterCache = {
        groups: []
      };

      doc.querySelectorAll('.filter-group').forEach(filterGroup => {
        const label = filterGroup.querySelector('.filter-group__title span')?.textContent?.trim();
        const options = [];

        filterGroup.querySelectorAll('.filter-option[data-filter-value]').forEach(option => {
          options.push({
            value: option.dataset.filterValue,
            paramName: option.dataset.filterParam,
            html: option.outerHTML
          });
        });

        if (label && options.length > 0) {
          this.filterCache.groups.push({ label, options });
        }
      });
    },

    reconcileFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      const checkboxes = document.querySelectorAll('#collection-filters input[type="checkbox"][name][value]');

      checkboxes.forEach(checkbox => {
        const urlValues = urlParams.getAll(checkbox.name);
        checkbox.checked = urlValues.includes(checkbox.value);
      });

      this.reconcileABVRadios(urlParams);
      this.reconcilePriceRadios(urlParams);

      this.urlChanged = !this.urlChanged;
    },

    reconcileABVRadios(urlParams) {
      document.querySelectorAll('[data-abv-range]').forEach(label => {
        const radio = label.querySelector('input[type="checkbox"]');
        if (!radio) return;

        const range = label.dataset.abvRange.split('-');
        const minABV = parseFloat(range[0]);
        const maxABV = parseFloat(range[1]);

        const abvContainer = label.closest('.filter-group__content');
        const hiddenInputs = abvContainer?.querySelectorAll('input[data-abv]') || [];

        const activeAbvValues = [];
        hiddenInputs.forEach(input => {
          const urlValues = urlParams.getAll(input.name);
          if (urlValues.includes(input.value)) {
            activeAbvValues.push(parseFloat(input.dataset.abv));
          }
        });

        const allInRange = activeAbvValues.length > 0 && activeAbvValues.every(abv => abv >= minABV && abv <= maxABV);
        radio.checked = allInRange;
      });
    },

    reconcilePriceRadios(urlParams) {
      document.querySelectorAll('[data-price-range]').forEach(label => {
        const radio = label.querySelector('input[type="checkbox"]');
        if (!radio) return;

        const range = label.dataset.priceRange;
        const minParamName = 'filter.v.price.gte';
        const maxParamName = 'filter.v.price.lte';

        const urlMin = urlParams.get(minParamName);
        const urlMax = urlParams.get(maxParamName);

        let isSelected = false;

        if (range === '75-') {
          isSelected = urlMin === '75' && !urlMax;
        } else {
          const [min, max] = range.split('-').map(v => v || null);
          isSelected = urlMin === min && urlMax === max;
        }

        radio.checked = isSelected;
      });
    },

    async updateURLAndFetch(urlParams) {
      const paramsString = urlParams.toString();
      const url = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
      history.pushState({ params: paramsString }, '', url);

      this.reconcileFromURL();
      await this.fetchAndUpdateProducts(paramsString);
    },

    async fetchAndUpdateProducts(params) {
      const collectionSection = document.querySelector('.collection')?.closest('.shopify-section');
      const sectionId = collectionSection ? collectionSection.id.replace('shopify-section-', '') : null;

      if (!sectionId) return;

      const fetchUrl = `${window.location.pathname}?section_id=${sectionId}&${params}`;
      const response = await fetch(fetchUrl);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      this.updateProductGrid(doc);
      this.updateProductCount(doc);
      this.updatePagination(doc);
      await this.updateFilterAvailability(doc);
    },

    async updateFilterAvailability(docWithCurrentFilters) {
      const currentOptions = document.querySelectorAll('.filter-option[data-filter-value]');

      currentOptions.forEach(option => {
        const value = option.dataset.filterValue;
        const paramName = option.dataset.filterParam;

        const fetchedOption = docWithCurrentFilters.querySelector(
          `.filter-option[data-filter-value="${value}"][data-filter-param="${paramName}"]`
        );

        const isAvailable = fetchedOption && !fetchedOption.classList.contains('filter-option--unavailable');

        const checkbox = option.querySelector('input[type="checkbox"]');
        if (checkbox && !checkbox.checked) {
          checkbox.disabled = !isAvailable;
          option.classList.toggle('filter-option--unavailable', !isAvailable);
        }
      });

      requestAnimationFrame(() => this.sortFilterOptions());
    },

    sortFilterOptions() {
      document.querySelectorAll('.filter-group__content').forEach(container => {
        const options = Array.from(container.querySelectorAll('.filter-option[data-filter-value]'));

        if (options.length === 0) return;

        options.sort((a, b) => {
          const aDisabled = a.classList.contains('filter-option--unavailable');
          const bDisabled = b.classList.contains('filter-option--unavailable');

          if (aDisabled && !bDisabled) return 1;
          if (!aDisabled && bDisabled) return -1;

          const aValue = a.dataset.filterValue || '';
          const bValue = b.dataset.filterValue || '';

          const aNum = parseFloat(aValue);
          const bNum = parseFloat(bValue);

          if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
          }

          return aValue.localeCompare(bValue, undefined, { numeric: true });
        });

        options.forEach(option => container.appendChild(option));
      });
    },

    updateProductGrid(doc) {
      const newProducts = doc.querySelector('.collection-products');
      const currentProducts = document.querySelector('.collection-products');
      if (newProducts && currentProducts) {
        currentProducts.innerHTML = newProducts.innerHTML;
      }
    },

    updateProductCount(doc) {
      const newCount = doc.querySelector('.collection-product-count');
      const currentCount = document.querySelector('.collection-product-count');
      if (newCount && currentCount) {
        const textNode = Array.from(currentCount.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
        const newTextNode = Array.from(newCount.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
        if (textNode && newTextNode) {
          textNode.textContent = newTextNode.textContent;
        }
      }
    },

    updatePagination(doc) {
      const newPagination = doc.querySelector('.pagination');
      const currentPagination = document.querySelector('.pagination');
      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
      } else if (!newPagination && currentPagination) {
        currentPagination.remove();
      }
    },

    applySort(sortValue) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('sort_by', sortValue);

      this.updateURLAndFetch(urlParams);
    },

    clearAll() {
      const urlParams = new URLSearchParams(window.location.search);
      const sortBy = urlParams.get('sort_by');

      const newParams = new URLSearchParams();
      if (sortBy) {
        newParams.set('sort_by', sortBy);
      }

      this.updateURLAndFetch(newParams);
    },

    clearFilterGroup(filterGroupElement) {
      const urlParams = new URLSearchParams(window.location.search);
      const checkboxes = filterGroupElement.querySelectorAll('input[type="checkbox"][name]');

      checkboxes.forEach(checkbox => {
        if (checkbox.name) {
          urlParams.delete(checkbox.name);
        }
      });

      this.updateURLAndFetch(urlParams);
    },

    isFilterGroupActive(filterGroupElement) {
      this.urlChanged;
      const urlParams = new URLSearchParams(window.location.search);

      const radios = filterGroupElement.querySelectorAll('input[type="checkbox"][data-abv-range], input[type="checkbox"][data-price-range]');
      if (radios.length > 0) {
        return Array.from(radios).some(radio => radio.checked);
      }

      const checkboxes = filterGroupElement.querySelectorAll('input[type="checkbox"][name]');
      return Array.from(checkboxes).some(checkbox => {
        const urlValues = urlParams.getAll(checkbox.name);
        return urlValues.includes(checkbox.value);
      });
    },

    hasAnyFilters() {
      this.urlChanged;
      const urlParams = new URLSearchParams(window.location.search);

      for (const [key] of urlParams) {
        if (key.startsWith('filter.')) {
          return true;
        }
      }

      return false;
    },




  });

  Alpine.data('productRecommendations', (productId) => ({
    hasProducts: false,
    loading: true,

    async init() {
      try {
        const recommendationsUrl = `/recommendations/products?section_id=product-recommendations&product_id=${productId}&limit=4`;
        const response = await fetch(recommendationsUrl);
        const html = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const cards = doc.querySelectorAll('.product-card');

        if (cards.length > 0) {
          this.hasProducts = true;
          const grid = this.$el.querySelector('.product-recommendations__grid');

          if (grid) {
            cards.forEach(card => grid.appendChild(card));
          }
        }
      } catch (error) {
        console.error('Failed to load recommendations:', error);
      } finally {
        this.loading = false;
      }
    }
  }));
});
